import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ImpersonationBroker, ITemplatePreview } from '../../viewmodels/AppViewModels';
import { reactQueryClient } from '../../web/ReactQueryProvider';

export const MY_TEMPLATES_FILTER_INFINITE_KEY = 'myTemplateFilterInfiniteKey';

export const useMyTemplatesFilterInfiniteQuery = ({
	impersonationContext,
	enabled = true,
	excludeExpired = true,
	industry,
	pageSize = 50,
	scopeFilter,
	sort = 'desc',
	sortBy = 'creationDate',
	term = '',
	templateType = Api.TemplateType.Email,
}: {
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
	excludeExpired?: boolean;
	industry: Api.Industry;
	pageSize?: number;
	scopeFilter?: Api.TemplateScopeFilter;
	sort?: 'asc' | 'desc';
	sortBy?: string;
	term?: string;
	templateType?: Api.TemplateType;
}) => {
	const userSession = useUserSession();
	return useInfiniteQuery({
		enabled,
		queryFn: ({ pageParam }) => {
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<ITemplatePreview>>(
				ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: {
						excludeExpired,
						pageSize,
						pageToken: pageParam,
						scopeFilter,
						search: term,
						sort,
						sortBy,
						templateType,
					},
					urlPath: `template/${encodeURIComponent(industry)}/me/filter/paged`,
				}),
				'GET'
			);
		},
		getNextPageParam: lastPage => lastPage.pageToken || undefined,
		initialPageParam: '',
		queryKey: [
			MY_TEMPLATES_FILTER_INFINITE_KEY,
			templateType,
			excludeExpired,
			industry,
			sort,
			sortBy,
			scopeFilter,
			term,
			pageSize,
			// Without these, switching the impersonated account serves the previous account's templates from cache.
			impersonationContext?.account?.id,
			impersonationContext?.user?.id,
		],
	});
};

export const invalidateAllMyTemplatesFilterInfinite = () => {
	reactQueryClient.invalidateQueries({ queryKey: [MY_TEMPLATES_FILTER_INFINITE_KEY] });
};
